/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CartItemTile from '@tcp/core/src/components/features/CnC/CartItemTile/container/CartItemTile.container';
import {
  getProductDetails,
  getItemInfoDetails,
  getProductQty,
  getProductItemPartNumber,
  getIsOnlineOnly,
  getStoreSpecials,
} from '@tcp/core/src/components/features/CnC/CartItemTile/container/CartItemTile.selectors';
import { FULLY_VISIBLE } from '@tcp/core/src/constants/rum.constants';
import RenderPerf from '@tcp/web/src/components/common/molecules/RenderPerf';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty.js';
import { getIconPath, routerPush } from '@tcp/core/src/utils';
import {
  capitalizeWords,
  dateFormatter,
  getDateObject,
  getESTDateTime,
  getFormattedPreOrderDateCartPage,
} from '@tcp/core/src/utils/utils';
import Image from '@tcp/core/src/components/common/atoms/Image';
import EmptyBag from '@tcp/core/src/components/features/CnC/EmptyBagPage/views/EmptyBagPage.view';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import MiniBagTileSkeleton from '@tcp/core/src/components/features/CnC/CartItemTile/molecules/CartItemTile/skelton/MiniBagTileSkeleton.view';
import styles, {
  productTileCss,
  miniBagCSS,
  ViewAll,
  ShowLessText,
} from '../styles/ProductTileWrapper.style';
import ProductTileGroup from './ProductTileGroup.view';
import CARTPAGE_CONSTANTS from '../../../CartItemTile.constants';
import CartItemTileSkelton from '../../../molecules/CartItemTile/skelton/CartItemTileSkelton.view';
import CartItemTileLabels, {
  CHECKOUT_LABELS,
  GLOBAL_LABELS,
} from '../../../molecules/CartItemTile/views/CartItemTile.labels';
import { getQuickViewLabelsCopy } from '../../../../../../common/organisms/QuickViewModal/container/QuickViewModal.selectors';
import {
  PRODUCT_PICKUP_LABELS,
  labelsObject,
  shippingLabelFromGenericLabels,
} from '../../../../../../common/organisms/PickupStoreModal/molecules/PickupStoreSelectionForm/views/PickupStoreSelectionForm.labels';
import {
  QUICK_VIEW_LABELS,
  quickViewLabels as qvLabels,
} from '../../../../../../common/organisms/QuickViewModal/Labels/QuickView.labels';
import {
  GLOBAL_COMMON_BROWSE_LABELS,
  globalCommonBrowseLabels,
} from '../../../../../../../utils/labels/globalCommonBrowseLabels.labels';
import {
  GLOBAL_ACCESSIBILITY_LABELS,
  GlobalAccessibilityLabels,
} from '../../../../../../../utils/labels/globalAccessibility.labels';
import { GlobalGenericLabels } from '../../../../../../../utils/labels/globalGeneric.labels';
import { GlobalAddToBagLabels } from '../../../../../../../utils/labels/globalAddedToBag.labels';
import globalCartItemTileLabels from '../../../../../../../utils/labels/globalCartItemTile.labels';

/**
 * This just holds the logic for rendering a UX timer
 * to measure when the cart items appear. It reads the
 * number of cart items and renders the timer if that
 * number is greater than zero.
 */

function CartItemsUXTimer({ orderItems }) {
  const [state, setState] = useState(false);
  useEffect(() => {
    if (orderItems.size > 0) setState(true);
  }, [orderItems.size]);
  return state ? <RenderPerf.Measure name={FULLY_VISIBLE} /> : null;
}

CartItemsUXTimer.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  orderItems: PropTypes.any.isRequired,
};

const getGroupItemsQty = (groupedItem) => {
  let itemsQty = 0;
  const { items = [] } = groupedItem || {};
  items.forEach((item) => {
    itemsQty += getProductQty(item);
  });
  return itemsQty;
};

const getTotalItemsQty = (groupedItems) => {
  let totalItemsCount = 0;
  groupedItems.forEach((groupedItem) => {
    totalItemsCount += getGroupItemsQty(groupedItem);
  });
  return totalItemsCount;
};

const getIsUnavailable = (productDetail) => {
  return (
    productDetail.miscInfo.availability === CARTPAGE_CONSTANTS.AVAILABILITY_UNAVAILABLE ||
    (productDetail.miscInfo.orderItemType === CARTPAGE_CONSTANTS.BOSS &&
      !productDetail.miscInfo.isBossEligible)
  );
};

const getBossUnavailable = ({
  productDetail,
  isBossEnabled,
  isBossCartPage,
  bossUnavailableSwitch,
}) => {
  const {
    miscInfo: { availability, availabilitySTH, isBossEligible, accessories },
    itemInfo: { preOrderDate, isGiftCard },
  } = productDetail;
  if (
    isBossCartPage &&
    bossUnavailableSwitch &&
    availabilitySTH === CARTPAGE_CONSTANTS.AVAILABILITY.OK &&
    (!accessories || accessories?.size === 0)
  ) {
    return (
      !isBossEligible ||
      !isBossEnabled ||
      isGiftCard ||
      preOrderDate ||
      availability === CARTPAGE_CONSTANTS.AVAILABILITY.UNAVAILABLE
    );
  }

  return false;
};

export const getDateRangeForPreOrderItems = (preOrderDates) => {
  const maxDateObj = getDateObject(Math.max.apply(null, preOrderDates));
  const minDateObj = getDateObject(Math.min.apply(null, preOrderDates));
  let rangeDate = null;
  if (maxDateObj && minDateObj) {
    rangeDate = `${getFormattedPreOrderDateCartPage(
      minDateObj,
      true
    )} - ${getFormattedPreOrderDateCartPage(maxDateObj, true)}`;
  }
  if (minDateObj.getTime() === maxDateObj.getTime()) {
    rangeDate = getFormattedPreOrderDateCartPage(minDateObj, true);
  }
  return rangeDate;
};

const getItemQuantityMap = (items) => {
  if (items && (items.length || items.size)) {
    return items.map((itm) => {
      return {
        productId: getProductItemPartNumber(itm),
        qty: getProductQty(itm),
        isOnlineOnly: getIsOnlineOnly(itm),
        storeSpecials: getStoreSpecials(itm),
      };
    });
  }
  return [];
};

const showPickupTile = (
  fullfilmentGroup,
  shipmentProductInventory,
  showBopisTileBasedOnStoreSpecials
) => {
  const { orderType, items } = fullfilmentGroup;
  if (orderType === CARTPAGE_CONSTANTS.BOPIS) return true;
  const itemMap = getItemQuantityMap(items);
  if (itemMap?.length > 0 && shipmentProductInventory?.length > 0) {
    return itemMap?.every((item) => {
      const isProductEligibleForBopis = showBopisTileBasedOnStoreSpecials
        ? item.storeSpecials || !item.isOnlineOnly
        : !item.isOnlineOnly;
      const lookupItem = shipmentProductInventory.find(
        (inventoryItem) => inventoryItem.productID === item.productId
      );
      if (lookupItem) {
        return isProductEligibleForBopis && lookupItem.quantity >= item.qty;
      }
      return false;
    });
  }
  return false;
};

const showToggleLink = (
  product,
  shipmentProductInventory,
  isBopisEnabled,
  isBossEnabled,
  showBopisTileBasedOnStoreSpecials
) => {
  const { productInfo = {}, miscInfo = {}, itemInfo = {} } = product || {};
  const { itemPartNumber } = productInfo;
  const { isOnlineOnly, availability, orderItemType, storeSpecials } = miscInfo;
  const { qty } = itemInfo;
  const isProductEligibleForBopis = showBopisTileBasedOnStoreSpecials
    ? storeSpecials || !isOnlineOnly
    : !isOnlineOnly;

  const isOnlineOnlyProduct = showBopisTileBasedOnStoreSpecials
    ? storeSpecials !== undefined && !storeSpecials && isOnlineOnly
    : isOnlineOnly;

  let toggleLink = false;
  const { AVAILABILITY, BOPIS, BOSS } = CARTPAGE_CONSTANTS;
  if (orderItemType === BOSS) {
    toggleLink = isBossEnabled && !!toggleLink;
  } else if (orderItemType === BOPIS) {
    toggleLink = isBopisEnabled && (isOnlineOnlyProduct || availability !== AVAILABILITY.OK);
  } else if (shipmentProductInventory?.length > 0) {
    toggleLink = shipmentProductInventory?.find(
      (item) =>
        item.productID === itemPartNumber && isProductEligibleForBopis && item.quantity >= qty
    );
  }
  return toggleLink;
};

class ProductTileWrapper extends React.PureComponent {
  globalLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LABELS, CartItemTileLabels);

  checkoutLabels = getLabelFromCopyOrFromTranslation(CHECKOUT_LABELS, CartItemTileLabels);

  qvLabelsObj = {
    ...getLabelFromCopyOrFromTranslation(QUICK_VIEW_LABELS, qvLabels),
    ...getLabelFromCopyOrFromTranslation(GLOBAL_COMMON_BROWSE_LABELS, globalCommonBrowseLabels),
    ...getLabelFromCopyOrFromTranslation(GLOBAL_ACCESSIBILITY_LABELS, GlobalAccessibilityLabels),
    lbl_shipping: getLabelFromCopyOrFromTranslation(
      shippingLabelFromGenericLabels,
      GlobalGenericLabels
    ),
    lbl_Product_pickup_SELECTED_STORE: getLabelFromCopyOrFromTranslation(
      `${PRODUCT_PICKUP_LABELS}.lbl_Product_pickup_SELECTED_STORE`,
      labelsObject
    ),
    lbl_pickup_instead_free_order: getLabelFromCopyOrFromTranslation(
      `global.addedToBagModal.lbl_pickup_instead_free_order`,
      GlobalAddToBagLabels
    ),
    lbl_cartTile_pickUpToday: getLabelFromCopyOrFromTranslation(
      `global.cartItemTile.lbl_cartTile_pickUpToday`,
      globalCartItemTileLabels
    ),
  };

  constructor(props) {
    super(props);
    this.state = {
      isEditAllowed: true,
      openedTile: 0,
      swipedElement: null,
      loginModal: false,
      showShowLess: false,
    };
    this.currentPagePos = null;
  }

  toggleEditAllowance = () => {
    const { isEditAllowed } = this.state;
    const { onItemEdit } = this.props;
    if (onItemEdit) {
      onItemEdit(isEditAllowed);
    }
    this.setState({
      isEditAllowed: !isEditAllowed,
    });
  };

  componentDidUpdate(prevProps, prevState) {
    const { isScrollToTile, setScrollToTile } = this.props;
    const { showShowLess } = this.state;
    const { showShowLess: prevShouldShowLess } = prevState;
    if (isScrollToTile === true) {
      const firstTileDiv = document.getElementsByClassName('cartPageTitleHeader')[0];
      firstTileDiv.scrollIntoView();
      setScrollToTile(false);
    }
    if (showShowLess !== prevShouldShowLess && showShowLess === false) {
      window.scrollTo(0, this.currentPagePos);
    }
  }

  toggleLoginModal = () => {
    const { loginModal } = this.state;
    this.setState({
      loginModal: !loginModal,
    });
  };

  setSwipedElement = (elem) => {
    this.setState({ swipedElement: elem });
  };

  setSelectedProductTile = ({ index }) => {
    this.setState({ openedTile: index });
  };

  isEditAllowed = (productDetail, pageView) => {
    const { isEditAllowed } = this.state;
    if (
      getIsUnavailable(productDetail) ||
      productDetail.miscInfo.availability === CARTPAGE_CONSTANTS.AVAILABILITY_SOLDOUT ||
      (pageView !== 'myBag' && productDetail.miscInfo.orderItemType === CARTPAGE_CONSTANTS.BOPIS)
    ) {
      return false;
    }
    return isEditAllowed;
  };

  onLinkClick = ({ e, componentId }) => {
    const { openOverlay, fullPageAuthEnabled } = this.props;
    const { pathname = 'referrer' } = (window && window.location) || {};
    e.preventDefault();
    if (fullPageAuthEnabled && componentId === 'login') {
      routerPush(`/home?target=login&successtarget=${pathname}`, '/home/login');
    } else {
      openOverlay({
        component: componentId,
        variation: 'primary',
      });
    }
  };

  getTickIcon = () => {
    return <Image alt="closeIcon" className="tick-icon" src={getIconPath('circle-check-fill')} />;
  };

  renderEmptyBag = ({
    cartData,
    isUserLoggedIn,
    isBagPageSflSection,
    showPlccApplyNow,
    isBagPage,
    openModalApplyNowModal,
    spcBag,
    newBag,
    navigation,
    route,
  }) => {
    const { isMiniBag, isBagLoading, rememberedUser } = this.props;
    const { loginModal } = this.state;
    if (isBagLoading && isBagPage) {
      return (
        <>
          <CartItemTileSkelton />
          <CartItemTileSkelton />
        </>
      );
    }
    if (cartData?.length === 0 || cartData?.size === 0) {
      return (
        <EmptyBag
          isUserLoggedIn={isUserLoggedIn}
          isBagPageSflSection={isBagPageSflSection}
          showPlccApplyNow={showPlccApplyNow}
          onLinkClick={this.onLinkClick}
          openModalApplyNowModal={openModalApplyNowModal}
          toggleLoginModal={this.toggleLoginModal}
          loginModal={loginModal}
          spcBag={spcBag}
          newBag={newBag}
          navigation={navigation}
          route={route}
          rememberedUser={rememberedUser}
        />
      );
    }
    if (isMiniBag) {
      return (
        <>
          <MiniBagTileSkeleton />
          <MiniBagTileSkeleton />
        </>
      );
    }
    return null;
  };

  renderItemRows = ({
    idx,
    productDetail,
    inheritedStyles,
    openedTile,
    swipedElement,
    showToggleLinkProductTile,
    pickUpTileEnabled,
    isNonBossCart,
    getBossUnavailableItems,
    orderItems,
    isBossUnavailable,
  }) => {
    const {
      newBag,
      newMiniBag,
      isLowInventoryBannerEnabled,
      isLowInventoryBannerEnabledForOldDesign,
      isPlcc,
      sflItemsCount,
      closeMiniBag,
      navigation,
      pageView,
      isBagPageSflSection,
      isMiniBag,
      spcBag,
      isCartQuantityUpdating,
      shippingMethodSelected,
      orderEddDates,
      userDefaultStore,
      updateStoreItem,
      LoyaltyPoints,
      orderAccessorieItems,
      isDeleteAnimationEnabled,
      isDesktopProductTileCleanUpDisabled,
      cartProductsWithPriceDrop,
      sflProductsWithPriceDrop,
      isPriceDropNotificationEnabled,
    } = this.props;
    return (
      <CartItemTile
        inheritedStyles={inheritedStyles}
        productDetail={productDetail}
        key={productDetail.itemInfo.itemId}
        pageView={pageView}
        toggleEditAllowance={this.toggleEditAllowance}
        isEditAllowed={this.isEditAllowed(productDetail, pageView)}
        isPlcc={isPlcc}
        itemIndex={idx}
        openedTile={openedTile}
        setSelectedProductTile={this.setSelectedProductTile}
        setSwipedElement={this.setSwipedElement}
        swipedElement={swipedElement}
        sflItemsCount={sflItemsCount}
        isBagPageSflSection={isBagPageSflSection}
        navigation={navigation}
        closeMiniBag={closeMiniBag}
        isMiniBag={isMiniBag}
        isMultipackProduct={productDetail.productInfo.multipack}
        spcBag={spcBag}
        newBag={newBag}
        newMiniBag={newMiniBag}
        isLowInventoryBannerEnabled={isLowInventoryBannerEnabled}
        isLowInventoryBannerEnabledForOldDesign={isLowInventoryBannerEnabledForOldDesign}
        isCartQuantityUpdating={isCartQuantityUpdating}
        orderEddDates={orderEddDates}
        shippingMethodSelected={shippingMethodSelected}
        userDefaultStore={userDefaultStore}
        updateStoreItem={updateStoreItem}
        showToggleLinkProductTile={showToggleLinkProductTile}
        hideCartItemBoxButtons
        LoyaltyPoints={LoyaltyPoints}
        pickUpTileEnabled={pickUpTileEnabled}
        isNonBossCart={isNonBossCart}
        orderAccessorieItems={orderAccessorieItems}
        isDeleteAnimationEnabled={isDeleteAnimationEnabled}
        getBossUnavailableItems={getBossUnavailableItems}
        totalItems={orderItems}
        isBossUnavailableValue={isBossUnavailable}
        isDesktopProductTileCleanUpDisabled={isDesktopProductTileCleanUpDisabled}
        cartProductsWithPriceDrop={cartProductsWithPriceDrop}
        sflProductsWithPriceDrop={sflProductsWithPriceDrop}
        isPriceDropNotificationEnabled={isPriceDropNotificationEnabled}
      />
    );
  };

  viewAllHandler = (e) => {
    e.preventDefault();
    this.currentPagePos = window.scrollY;
    this.setState({ showShowLess: true });
  };

  showLessHandler = (e) => {
    e.preventDefault();
    this.setState({ showShowLess: false });
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
  render() {
    const {
      orderItems,
      orderItemsLocal,
      pageView,
      confirmRemoveCartItem,
      isUserLoggedIn,
      isBagPageSflSection,
      isCartItemsUpdating,
      sflItems,
      showPlccApplyNow,
      isCartItemSFL,
      isSflItemRemoved,
      isSflItemMovedToBag,
      setHeaderErrorState,
      openModalApplyNowModal,
      isMiniBag,
      isBagLoading,
      fullPageAuthEnabled,
      spcBag,
      groupedItems,
      className,
      isNewQVEnabled,
      isBossEnabled,
      userDefaultStore,
      shippingMethodSelected,
      newBag,
      updateStoreItem,
      orderEddDates,
      updateStoreLoading,
      setStoreUpdateCompletedState,
      updateStoreCompleted,
      isPlcc,
      navigation,
      sflItemsCount,
      closeMiniBag,
      newMiniBag,
      isLowInventoryBannerEnabledForOldDesign,
      isLowInventoryBannerEnabled,
      shipmentProductInventory,
      setClickAnalyticsDataDispatch,
      trackClickDispatch,
      onPickUpOpenClick,
      orderAccessorieItems,
      isBopisEnabled,
      updateDeliveryMethodIndex,
      route,
      setUserDefaultStore,
      isPreOrderItemsIncluded,
      onLayoutTileGroup,
      isDeleteAnimationEnabled,
      initialSFLItemsQty,
      isSocialProofingMsgEnabledApp,
      bossUnavailableSwitch,
      isBossCartPage,
      isBossCart,
      shipments,
      freeShippingThershold,
      shippingTotal,
      isDynamicFreeShippingEnabled,
      productTotal,
      isSiteWidePromoBannerAbTestEnabled,
      isExtendedHolidayReturnsBannerAbTestEnabled,
      isHolidayGetItByMessageBannerAbTestEnabled,
      isDefaultSocialProofBanner,
      setDefaultBannerAction,
      timerDeadlineValue,
      isDesktopProductTileCleanUpDisabled,
      isBopisPromoDiscountBannerEnabled,
      storeHoursThreshold,
      isDisplayPickupTimeEnabled,
      isFullDateDisplayEnabled,
      isInternationalShipping,
      isEnableShipmentTilesForInternationalSite,
      isMixedOrder,
      isMobileWebTileEnhancementEnabled,
      showBopisTileBasedOnStoreSpecials,
      sflProductsWithPriceDrop,
      isPriceDropNotificationEnabled,
    } = this.props;
    const quickViewLabels = getQuickViewLabelsCopy(this.qvLabelsObj);
    const { cartItemTile } = this.globalLabels || {};

    const cartData = isBagPageSflSection ? sflItems : groupedItems;
    let isUnavailable;
    let isBossUnavailable;
    let isSoldOut;
    const isBagPage = pageView === 'myBag';
    const inheritedStyles = isBagPage ? productTileCss : miniBagCSS;
    const getUnavailableOOSItems = [];
    const getBossUnavailableItems = [];
    const { openedTile, swipedElement, showShowLess } = this.state;

    if (
      (cartData && cartData.size > 0 && !isBagLoading && isBagPageSflSection) ||
      (isBagPageSflSection && orderItemsLocal && orderItemsLocal.size > 0 && isBagLoading)
    ) {
      const dataSource = isBagLoading && orderItemsLocal.size > 0 ? orderItemsLocal : cartData;
      const orderItemsView = dataSource.map((tile, index) => {
        const productDetail = getProductDetails(tile);

        if (productDetail.miscInfo.availability === CARTPAGE_CONSTANTS.AVAILABILITY_SOLDOUT) {
          getUnavailableOOSItems.push(productDetail.itemInfo.itemId);
          isSoldOut = true;
        }
        if (getIsUnavailable(productDetail)) {
          getUnavailableOOSItems.push(productDetail.itemInfo.itemId);
          isUnavailable = true;
        }
        if (
          getBossUnavailable({
            productDetail,
            isBossEnabled,
            isBossCartPage,
            bossUnavailableSwitch,
          })
        ) {
          getBossUnavailableItems.push(productDetail.itemInfo.itemId);
          isBossUnavailable = true;
        }

        productDetail.skuInfo = JSON.parse(JSON.stringify(tile)).skuInfo;
        return (
          <>
            <CartItemTile
              inheritedStyles={inheritedStyles}
              productDetail={productDetail}
              key={productDetail?.itemInfo?.itemId}
              pageView={pageView}
              toggleEditAllowance={this.toggleEditAllowance}
              isEditAllowed={this.isEditAllowed(productDetail, pageView)}
              isPlcc={isPlcc}
              itemIndex={index}
              openedTile={openedTile}
              setSelectedProductTile={this.setSelectedProductTile}
              setSwipedElement={this.setSwipedElement}
              swipedElement={swipedElement}
              sflItemsCount={sflItemsCount}
              isBagPageSflSection={isBagPageSflSection}
              navigation={navigation}
              closeMiniBag={closeMiniBag}
              isMiniBag={isMiniBag}
              isMultipackProduct={productDetail.productInfo.multipack}
              spcBag={spcBag}
              newBag={newBag}
              newMiniBag={newMiniBag}
              isLowInventoryBannerEnabled={isLowInventoryBannerEnabled}
              isLowInventoryBannerEnabledForOldDesign={isLowInventoryBannerEnabledForOldDesign}
              orderAccessorieItems={orderAccessorieItems}
              userDefaultStore={userDefaultStore}
              sflItems={sflItems}
              setUserDefaultStore={setUserDefaultStore}
              isDeleteAnimationEnabled={isDeleteAnimationEnabled}
              getBossUnavailableItems={getBossUnavailableItems}
              totalItems={orderItems}
              isBossUnavailableValue={isBossUnavailable}
              isDesktopProductTileCleanUpDisabled={isDesktopProductTileCleanUpDisabled}
              sflProductsWithPriceDrop={sflProductsWithPriceDrop}
              isPriceDropNotificationEnabled={isPriceDropNotificationEnabled}
            />
          </>
        );
      });
      return (
        <>
          {setHeaderErrorState &&
            setHeaderErrorState(true, {
              orderItems,
              pageView,
              isUnavailable,
              isSoldOut,
              getUnavailableOOSItems,
              confirmRemoveCartItem,
              isBagPageSflSection,
              isCartItemSFL,
              isCartItemsUpdating,
              isSflItemRemoved,
              isSflItemMovedToBag,
              isBossUnavailable,
              getBossUnavailableItems,
            })}
          {showShowLess ? orderItemsView : orderItemsView.slice(0, initialSFLItemsQty)}
          {/* UX timer */}
          {sflItemsCount > initialSFLItemsQty &&
            !showShowLess &&
            (isDesktopProductTileCleanUpDisabled ? (
              <ViewAll
                color="black"
                role="button"
                className="elem-mt-MED view-all-sfl-btn"
                onClick={this.viewAllHandler}
                fullWidth
                buttonVariation="fixed-width"
                borderRadius="6"
              >
                {`${renderLabel(cartItemTile?.lbl_view_all, 'lbl_view_all')} (${
                  sflItemsCount - initialSFLItemsQty
                })`}
              </ViewAll>
            ) : (
              <div className="show-less-block">
                <ShowLessText
                  anchorVariation="primary"
                  text={`${renderLabel(cartItemTile?.lbl_view_all, 'lbl_view_all')}`}
                  onClick={this.viewAllHandler}
                  underline
                />
              </div>
            ))}

          {showShowLess && sflItemsCount > initialSFLItemsQty && (
            <div className="show-less-block">
              <ShowLessText
                anchorVariation="primary"
                text={capitalizeWords(this.checkoutLabels.bagPage?.lbl_couponlist_lessMoreTxt)}
                onClick={this.showLessHandler}
                underline
              />
            </div>
          )}
          {!isMobileWebTileEnhancementEnabled && <CartItemsUXTimer orderItems={orderItems} />}
        </>
      );
    }
    if (cartData && cartData.length > 0 && !isBagLoading) {
      let firstItemInfo;
      const dataSource = cartData;
      const { BOPIS, BOSS } = CARTPAGE_CONSTANTS;
      const hasBossOrBopisItem = dataSource?.some((item) => item.orderType === (BOPIS || BOSS));
      const isBossCartValue = dataSource?.every((item) => item.orderType === BOSS);
      const isBopisCart = dataSource?.every((item) => item.orderType === BOPIS);
      const isNonBossCart = !isBossCartValue;
      const totalItemsQty = getTotalItemsQty(groupedItems);
      const orderItemsView = dataSource.map((tile, index) => {
        const pickUpTileEnabled = showPickupTile(
          tile,
          shipmentProductInventory,
          showBopisTileBasedOnStoreSpecials
        );
        const orderType = tile?.orderType;
        const groupItemsQty = getGroupItemsQty(tile);
        const preOrderDates = [];
        let isAllPreorderItems = true;
        // eslint-disable-next-line complexity
        const groupItem = tile?.items?.map((item, idx) => {
          if (idx === 0) {
            firstItemInfo = getItemInfoDetails(item);
          }
          const productDetail = getProductDetails(item);
          const showToggleLinkProductTile = showToggleLink(
            productDetail,
            shipmentProductInventory,
            isBopisEnabled,
            isBossEnabled,
            showBopisTileBasedOnStoreSpecials
          );

          if (
            (productDetail?.miscInfo?.variantPreOrderMap &&
              Object.keys(productDetail?.miscInfo?.variantPreOrderMap).length > 0) ||
            productDetail?.itemInfo?.preOrderDate
          ) {
            const variantPreOrderData =
              productDetail?.miscInfo?.variantPreOrderMap?.preOrderDate ||
              productDetail?.itemInfo?.preOrderDate;
            if (variantPreOrderData) {
              const POD = getDateObject(dateFormatter(variantPreOrderData));
              const today = getESTDateTime();
              const preOrderDate = today.getTime() > POD.getTime() ? today : POD;
              preOrderDates.push(preOrderDate);
            }
          } else if (
            productDetail?.itemInfo?.preOrderDate &&
            Object.keys(productDetail?.itemInfo?.preOrderDate).length > 0
          ) {
            const POD = getDateObject(dateFormatter(productDetail?.itemInfo?.preOrderDate));
            const today = getESTDateTime();
            const preOrderDate = today.getTime() > POD.getTime() ? today : POD;
            preOrderDates.push(preOrderDate);
          } else {
            isAllPreorderItems = false;
          }
          if (productDetail.miscInfo.availability === CARTPAGE_CONSTANTS.AVAILABILITY_SOLDOUT) {
            getUnavailableOOSItems.push(productDetail.itemInfo.itemId);
            isSoldOut = true;
          }
          if (getIsUnavailable(productDetail)) {
            getUnavailableOOSItems.push(productDetail.itemInfo.itemId);
            isUnavailable = true;
          }
          if (
            getBossUnavailable({
              productDetail,
              isBossEnabled,
              isBossCartPage,
              bossUnavailableSwitch,
            })
          ) {
            getBossUnavailableItems.push(productDetail.itemInfo.itemId);
            isBossUnavailable = true;
          }

          return (
            <>
              {this.renderItemRows({
                idx,
                productDetail,
                inheritedStyles,
                openedTile,
                swipedElement,
                showToggleLinkProductTile,
                pickUpTileEnabled,
                isNonBossCart,
                isBossCart,
                isBopisCart,
                setUserDefaultStore,
                getBossUnavailableItems,
                orderItems,
                isBossUnavailable,
              })}
            </>
          );
        });

        const preOrderDateRange =
          isAllPreorderItems &&
          preOrderDates.length > 0 &&
          getDateRangeForPreOrderItems(preOrderDates);

        return (
          <ProductTileGroup
            className={className}
            key={index.toString()}
            orderType={orderType}
            items={groupItem}
            shipment={tile.shipment}
            isNewQVEnabled={isNewQVEnabled}
            quickViewLabels={quickViewLabels}
            isBossEnabled={isBossEnabled}
            userDefaultStore={userDefaultStore}
            totalItemsQty={totalItemsQty}
            groupItemsQty={groupItemsQty}
            shippingMethodSelected={shippingMethodSelected}
            hasBossOrBopisItem={hasBossOrBopisItem}
            preOrderDates={preOrderDates}
            newBag={newBag}
            firstItemInfo={firstItemInfo}
            updateStoreItem={updateStoreItem}
            isMiniBag={isMiniBag}
            orderEddDates={orderEddDates}
            preOrderDateRange={preOrderDateRange}
            isAllPreorderItems={isAllPreorderItems}
            loading={updateStoreLoading}
            setStoreUpdateCompletedState={setStoreUpdateCompletedState}
            updateStoreCompleted={updateStoreCompleted}
            pickUpTileEnabled={pickUpTileEnabled}
            setClickAnalyticsDataDispatch={setClickAnalyticsDataDispatch}
            trackClickDispatch={trackClickDispatch}
            onPickUpOpenClick={onPickUpOpenClick}
            isBopisEnabled={isBopisEnabled}
            isBossCart={isBossCart}
            isBopisCart={isBopisCart}
            updateDeliveryMethodIndex={updateDeliveryMethodIndex}
            navigation={navigation}
            route={route}
            setUserDefaultStore={setUserDefaultStore}
            isPreOrderItemsIncluded={isPreOrderItemsIncluded}
            onLayoutTileGroup={onLayoutTileGroup}
            isSocialProofingMsgEnabledApp={isSocialProofingMsgEnabledApp}
            shipments={shipments}
            freeShippingThershold={freeShippingThershold}
            shippingTotal={shippingTotal}
            isDynamicFreeShippingEnabled={isDynamicFreeShippingEnabled}
            productTotal={productTotal}
            isSiteWidePromoBannerAbTestEnabled={isSiteWidePromoBannerAbTestEnabled}
            isExtendedHolidayReturnsBannerAbTestEnabled={
              isExtendedHolidayReturnsBannerAbTestEnabled
            }
            isHolidayGetItByMessageBannerAbTestEnabled={isHolidayGetItByMessageBannerAbTestEnabled}
            isDefaultSocialProofBanner={isDefaultSocialProofBanner}
            setDefaultBannerAction={setDefaultBannerAction}
            timerDeadlineValue={timerDeadlineValue}
            isDesktopProductTileCleanUpDisabled={isDesktopProductTileCleanUpDisabled}
            isBopisPromoDiscountBannerEnabled={isBopisPromoDiscountBannerEnabled}
            storeHoursThreshold={storeHoursThreshold}
            isDisplayPickupTimeEnabled={isDisplayPickupTimeEnabled}
            isFullDateDisplayEnabled={isFullDateDisplayEnabled}
            isInternationalShipping={isInternationalShipping}
            isEnableShipmentTilesForInternationalSite={isEnableShipmentTilesForInternationalSite}
            isMixedOrder={isMixedOrder}
            isMobileWebTileEnhancementEnabled={isMobileWebTileEnhancementEnabled}
          />
        );
      });
      return (
        <>
          {setHeaderErrorState &&
            setHeaderErrorState(true, {
              orderItems,
              pageView,
              isUnavailable,
              isSoldOut,
              getUnavailableOOSItems,
              confirmRemoveCartItem,
              isBagPageSflSection,
              isCartItemSFL,
              isCartItemsUpdating,
              isSflItemRemoved,
              isSflItemMovedToBag,
              isBossUnavailable,
              getBossUnavailableItems,
            })}
          {orderItemsView}
          {/* UX timer */}
          <CartItemsUXTimer orderItems={orderItems} />
        </>
      );
    }
    return (
      <>
        {setHeaderErrorState &&
          setHeaderErrorState(true, {
            orderItems,
            pageView,
            isUnavailable,
            isSoldOut,
            getUnavailableOOSItems,
            confirmRemoveCartItem,
            isBagPageSflSection,
            isCartItemSFL,
            isCartItemsUpdating,
            isSflItemRemoved,
            isSflItemMovedToBag,
            isBossUnavailable,
            getBossUnavailableItems,
          })}
        {this.renderEmptyBag({
          cartData,
          isUserLoggedIn,
          isBagPageSflSection,
          showPlccApplyNow,
          isBagPage,
          openModalApplyNowModal,
          isMiniBag,
          fullPageAuthEnabled,
          spcBag,
          navigation,
          route,
        })}
      </>
    );
  }
}

ProductTileWrapper.defaultProps = {
  orderItemsLocal: [],
  pageView: '',
  isBagPageSflSection: false,
  cartSflMerged: false,
  fullPageAuthEnabled: false,
  spcBag: false,
  newBag: false,
  isLowInventoryBannerEnabled: false,
  isLowInventoryBannerEnabledForOldDesign: false,
  className: '',
  isNewQVEnabled: false,
  isBossEnabled: false,
  isScrollToTile: false,
  orderEddDates: {},
  isBopisPromoDiscountBannerEnabled: false,
};

ProductTileWrapper.propTypes = {
  orderItems: PropTypes.shape([]).isRequired,
  orderItemsLocal: PropTypes.shape([]),
  sflItems: PropTypes.shape([]).isRequired,
  confirmRemoveCartItem: PropTypes.func.isRequired,
  isUserLoggedIn: PropTypes.bool.isRequired,
  isPlcc: PropTypes.bool.isRequired,
  pageView: PropTypes.string,
  sflItemsCount: PropTypes.number.isRequired,
  isBagPageSflSection: PropTypes.bool,
  showPlccApplyNow: PropTypes.bool.isRequired,
  isCartItemSFL: PropTypes.bool.isRequired,
  isSflItemRemoved: PropTypes.bool.isRequired,
  isMiniBag: PropTypes.bool.isRequired,
  onItemEdit: PropTypes.func.isRequired,
  openOverlay: PropTypes.func.isRequired,
  setHeaderErrorState: PropTypes.func.isRequired,
  openModalApplyNowModal: PropTypes.func.isRequired,
  closeMiniBag: PropTypes.func.isRequired,
  isBagLoading: PropTypes.bool.isRequired,
  isCartItemsUpdating: PropTypes.bool.isRequired,
  isSflItemMovedToBag: PropTypes.bool.isRequired,
  navigation: PropTypes.shape({}).isRequired,
  cartSflMerged: PropTypes.bool,
  fullPageAuthEnabled: PropTypes.bool,
  spcBag: PropTypes.bool,
  newBag: PropTypes.bool,
  isLowInventoryBannerEnabled: PropTypes.bool,
  isLowInventoryBannerEnabledForOldDesign: PropTypes.bool,
  shippingMethodSelected: PropTypes.shape({}).isRequired,
  className: PropTypes.string,
  isNewQVEnabled: PropTypes.bool,
  isBossEnabled: PropTypes.bool,
  userDefaultStore: PropTypes.shape({}).isRequired,
  orderEddDates: PropTypes.shape({}),
  onPickUpOpenClick: PropTypes.func.isRequired,
  isScrollToTile: PropTypes.bool,
  isBopisPromoDiscountBannerEnabled: PropTypes.bool,
};
export default withStyles(ProductTileWrapper, styles);
export { ProductTileWrapper as ProductTileWrapperVanilla };
